import * as React from 'react';
import Croppie from 'croppie';
import 'croppie/croppie.css';
import { Button, Icon, Modal } from '../..';

const CROPPIE_OPTIONS = {
  enableExif: true,
  showZoomer: true,
  viewport: {
    width: 200,
    height: 200,
    type: 'square' as const,
  },
};

type PhotoCropperDialogProps = {
  photo?: File;
  open: boolean;
  onClose: () => void;
  onSetCroppedPhoto: (blob: Blob) => void;
  isUploading?: boolean;
};

function PhotoCropperDialog({
  photo,
  open,
  onClose,
  onSetCroppedPhoto,
  isUploading,
}: PhotoCropperDialogProps) {
  const croppieRef = React.useRef<any>(null);
  const photoUrlRef = React.useRef<string | null>(null);

  // Use callback ref to initialize Croppie as soon as element is available
  const containerRef = React.useCallback(
    (node: HTMLDivElement | null) => {
      // Cleanup when node is removed
      if (!node) {
        if (croppieRef.current) {
          try {
            croppieRef.current.destroy();
          } catch {
            // Ignore cleanup errors
          }
          croppieRef.current = null;
        }
        if (photoUrlRef.current) {
          URL.revokeObjectURL(photoUrlRef.current);
          photoUrlRef.current = null;
        }
        return;
      }

      if (!open || !photo) {
        return;
      }

      // Clean up existing instance before creating new one
      if (croppieRef.current) {
        try {
          croppieRef.current.destroy();
        } catch {
          // Ignore cleanup errors
        }
        croppieRef.current = null;
      }

      // Wait for next tick to ensure DOM is ready
      setTimeout(() => {
        // Initialize new Croppie instance
        croppieRef.current = new Croppie(node, CROPPIE_OPTIONS);

        // Move the zoom slider to our custom wrapper
        setTimeout(() => {
          const sliderWrap = node.querySelector('.cr-slider-wrap');
          const customWrapper = document.querySelector('.cr-slider-wrapper');

          if (sliderWrap && customWrapper) {
            try {
              customWrapper.appendChild(sliderWrap);
              (sliderWrap as HTMLElement).style.display = 'flex';
              (sliderWrap as HTMLElement).style.margin = '0';
              (sliderWrap as HTMLElement).style.width = '100%';
            } catch {
              // Ignore if already moved
            }
          }
        }, 100);

        // Bind photo if available
        if (photo && croppieRef.current) {
          // Revoke previous URL if exists
          if (photoUrlRef.current) {
            URL.revokeObjectURL(photoUrlRef.current);
          }

          const fileURL = URL.createObjectURL(photo);
          photoUrlRef.current = fileURL;

          setTimeout(() => {
            if (croppieRef.current) {
              try {
                croppieRef.current.bind({ url: fileURL });
              } catch (error) {
                // eslint-disable-next-line no-console
                console.error('Error binding photo:', error);
              }
            }
          }, 200);
        }
      }, 50);
    },
    [open, photo],
  );

  // Cleanup on unmount
  React.useEffect(() => {
    return () => {
      if (croppieRef.current) {
        try {
          croppieRef.current.destroy();
        } catch {
          // Ignore cleanup errors
        }
        croppieRef.current = null;
      }
      if (photoUrlRef.current) {
        URL.revokeObjectURL(photoUrlRef.current);
        photoUrlRef.current = null;
      }
    };
  }, []);

  const handleCroppedPhoto = () => {
    if (!croppieRef.current) return;

    croppieRef.current
      .result({
        type: 'blob',
        size: {
          width: 400,
          height: 400,
        },
        format: 'jpeg',
        quality: 0.9,
      })
      .then((blob: Blob) => {
        onSetCroppedPhoto(blob);
      });
  };

  if (!open) return null;

  return (
    <Modal onClose={!isUploading ? onClose : undefined} show={open}>
      <div className="rounded-md bg-white tablet:w-[640px]">
        <div className="flex flex-col px-8 pt-5 pb-7">
          <h3 className="text-pos-black text-[20px] font-semibold mb-2">
            Edit Photo
          </h3>
          <p className="text-pos-gray text-xbase mb-6">
            Adjust and crop your product photo
          </p>

          <div className="relative">
            <div
              ref={containerRef}
              className="croppie relative flex flex-col items-center justify-center overflow-hidden"
            />
            <div className="relative mx-auto mt-4 flex w-9/12 items-center justify-center gap-3">
              <Icon
                id="minusSign"
                className="text-pos-gray h-5 w-5 flex-shrink-0 fill-pos-gray"
              />
              <div className="cr-slider-wrapper flex-1" />
              <Icon
                id="plusSign"
                className="text-pos-gray h-5 w-5 flex-shrink-0 fill-pos-gray"
              />
            </div>
          </div>

          <div className="mt-8 flex w-full flex-col items-center">
            <Button
              label={isUploading ? 'Uploading...' : 'Apply'}
              onClick={handleCroppedPhoto}
              disabled={isUploading}
              type="button"
              className="!bg-pos-green border border-[rgba(27,31,36,0.15)] min-w-[180px] h-[40px]
                items-center justify-center text-white text-xbase
                shadow-[0_1px_0_rgba(27,31,36,0.1),inset_0_1px_0_rgba(255,255,255,0.03)]
                disabled:bg-[#989d9c] disabled:text-white font-medium"
            />
          </div>
        </div>
      </div>

      <style>{`
        .croppie-container .cr-slider-wrap {
          display: none !important;
        }
        .cr-slider-wrapper {
          position: relative;
          display: flex;
          align-items: center;
        }
        .cr-slider-wrapper .cr-slider-wrap {
          display: flex !important;
          width: 100%;
          margin: 0 !important;
        }
        .cr-slider-wrapper .cr-slider {
          width: 100%;
        }
        .croppie-container .cr-viewport,
        .croppie-container .cr-resizer {
          border: 4px solid #fff !important;
        }
        .croppie-container .cr-boundary {
          width: 100% !important;
          height: 350px !important;
        }
      `}</style>
    </Modal>
  );
}

PhotoCropperDialog.defaultProps = {
  photo: undefined,
  isUploading: false,
};

export default PhotoCropperDialog;
